'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { format } from 'date-fns'
import { Blog } from '@/payload-types'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import clsx from 'clsx'
import { useLanguage } from '../../providers/LanguageProvider'
import { motion } from 'framer-motion'
import { useIsMobile } from '@/hooks/useIsMobile'

type Category = {
  title?: string
  [key: string]: unknown
}

function extractFirstCategoryTitle(categories: unknown): string {
  if (Array.isArray(categories)) {
    const first = categories[0]
    return typeof first === 'string' ? 'General' : (first as Category)?.title || 'General'
  }
  if (typeof categories === 'string') return 'General'
  if (categories && typeof categories === 'object' && 'title' in categories) {
    return (categories as Category).title || 'General'
  }
  return 'General'
}

export default function BlogClient() {
  const [blogs, setBlogs] = useState<Blog[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const { language } = useLanguage()
  const isRTL = language === 'ar'
  const isMobile = useIsMobile()

  // const visibleBlogs = blogs.slice(currentIndex, currentIndex + 2)
  const blogsPerPage = 2
  const totalPages = Math.ceil(blogs.length / blogsPerPage)
  const visibleBlogs = blogs.slice(
    currentIndex * blogsPerPage,
    currentIndex * blogsPerPage + blogsPerPage,
  )

  const hasPrev = currentIndex > 0
  const hasNext = currentIndex + 1 < totalPages

  const handlePrev = () => {
    if (hasPrev) setCurrentIndex((prev) => Math.max(0, prev - 1))
  }

  const handleNext = () => {
    if (hasNext) setCurrentIndex((prev) => prev + 1)
  }

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(`/api/blogs?locale=${language}`)
        const json = await res.json()
        setBlogs(json.docs || [])
      } catch (error) {
        console.error('Error fetching blogs:', error)
      }
    }

    fetchBlogs()
  }, [language])

  if (blogs.length === 0) {
    return (
      <div className="py-16 text-center bg-white text-gray-500">
        {language === 'ar' ? 'لا توجد مقالات متاحة حالياً' : 'No blogs available at the moment!'}
      </div>
    )
  }

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, delay: 0.3 + i * 0.2 },
    }),
  }

  return (
    <section className="w-full bg-white relative py-10 lg:py-16 2xl:py-20 mt-0 text-black">
      <img
        src="/images/container.svg"
        alt="svg"
        className={clsx(
          // Sizes
          'absolute size-56 sm:size-80 xl:size-80',
          // Top positions
          '-top-2 sm:-top-8 lg:-top-2 xl:top-14 2xl:top-16',
          // RTL-aware positioning and flipping
          isRTL ? 'left-0 scale-x-[-1]' : 'right-0',
        )}
      />
      <div className="section-container mx-auto mt-40">
        <div className="flex flex-col gap-4 sm:gap-5 md:gap-6 mb-5 sm:mb-6 md:mb-7 xl:mb-14">
          {/* Animated heading */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            viewport={{ once: true }}
            className=" text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px] text-[#414141] font-semibold"
          >
            {language === 'ar' ? 'من المدونة' : 'From The Blog'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-[#666666] font text-[15px] leading-5 sm:text-[15px] md:text-[16px] 2xl:text-lg"
          >
            {language === 'ar' ? (
              <>
                اكتشف مقالات ملهمة، وآراء الخبراء،
                <br />
                وآخر الاتجاهات في الصناعة.
              </>
            ) : (
              <>
                Discover thoughtful articles, expert perspectives, and the latest trends shaping the
                industry. <br className="hidden lg:block" />
                Whether you&apos;re curious, growing, or leading, there&apos;s something here for
                every step of your journey.
              </>
            )}
          </motion.p>
        </div>

        {/* Blog Cards with sliding effect */}
        {!isMobile && (
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                transform: isRTL
                  ? `translateX(${currentIndex * 100}%)`
                  : `translateX(-${currentIndex * 100}%)`,
              }}
            >
              {/* Wrapper for sliding */}
              {Array.from({ length: Math.ceil(blogs.length / 2) }).map((_, pageIndex) => {
                const items = blogs.slice(pageIndex * 2, pageIndex * 2 + 2)
                return (
                  <div
                    key={pageIndex}
                    className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-2 gap-5"
                  >
                    {items.map((blog, i) => {
                      const hasImage =
                        blog.heroImage &&
                        typeof blog.heroImage === 'object' &&
                        blog.heroImage.thumbnailURL

                      return (
                        <motion.div
                          key={blog.id}
                          variants={fadeInUp}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          custom={i}
                        >
                         <Link href="/blogs" className="block group">

                            <div className="relative w-full h-[200px] md:h-[250px] lg:h-[300px] xl:h-[350px] overflow-hidden mb-4">
                              {hasImage ? (
                                <Image   
                                  src={
                                    typeof blog.heroImage === 'object' &&
                                    blog.heroImage !== null &&
                                    blog.heroImage.thumbnailURL
                                      ? blog.heroImage.thumbnailURL
                                      : '/images/placeholder.png '
                                  }
                                  alt="No Image"
                                  fill
                                  className="object-cover transition-transform duration-300"
                                />
                              ) : (
                                <Image   
                                  src={'/images/placeholder.png'}
                                  alt="No Image"
                                  fill
                                  className="object-cover transition-transform duration-300"
                                />
                              )}
                            </div>
                            <div className="text-[13px]  mb-1 flex gap-2">
                              <span className="text-[#2759FB]">
                                {extractFirstCategoryTitle(blog.categories)}
                              </span>
                              <span className="text-gray-600">
                                {`| ${blog.publishedAt && format(new Date(blog.publishedAt), 'MMMM d, yyyy')}`}
                              </span>
                            </div>

                            <h3 className="md:text-[16px] 2xl:text-[32px] xl:text-[25px] xl:w-[81%]">
                              {blog.title}
                            </h3>
                          </Link>
                        </motion.div>
                      )
                    })}
                  </div>
                )
              })}
            </div>

            {/* Pagination Buttons */}
            {blogs.length > 2 && (
              <div className="flex justify-center mt-14 gap-4 ltr text-white" dir="ltr">
                {isRTL ? (
                  <>
                    <button
                      onClick={handleNext}
                      className={clsx(
                        'md:w-8 md:h-8 lg:size-8.5  flex items-center justify-center rounded-full transition border',
                        hasNext
                          ? 'bg-[#EE3E25] border-[#EE3E25] hover:bg-[#EE3E25]'
                          : 'bg-[#d7d7d7] border-[#d7d7d7] ',
                      )}
                      aria-label="Next"
                    >
                      <FiChevronLeft className="size-6" />
                    </button>

                    <button
                      onClick={handlePrev}
                      className={clsx(
                        'md:w-8 md:h-8 lg:size-8.5  flex items-center justify-center rounded-full transition border',
                        hasPrev
                          ? 'bg-[#EE3E25] border-[#EE3E25] hover:bg-[#EE3E25]'
                          : 'bg-[#d7d7d7] border-[#d7d7d7] ',
                      )}
                      aria-label="Previous"
                    >
                      <FiChevronRight className="size-6" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handlePrev}
                      className={clsx(
                        'md:w-8 md:h-8 lg:size-8.5  flex items-center justify-center rounded-full transition border',
                        hasPrev
                          ? 'bg-[#EE3E25] border-[#EE3E25] hover:bg-[#EE3E25]'
                          : 'bg-[#d7d7d7] border-[#d7d7d7]',
                      )}
                      aria-label="Previous"
                    >
                      <FiChevronLeft className="size-6" />
                    </button>

                    <button
                      onClick={handleNext}
                      className={clsx(
                        'md:w-8 md:h-8 lg:size-8.5  flex items-center justify-center rounded-full transition border',
                        hasNext
                          ? 'bg-[#EE3E25] border-[#EE3E25] hover:bg-[#EE3E25]'
                          : 'bg-[#d7d7d7] border-[#d7d7d7]',
                      )}
                      aria-label="Next"
                    >
                      <FiChevronRight className="size-6" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {isMobile && (
          <div className="overflow-x-auto scrollbar-none snap-x overflow-y-hidden flex gap-4 scroll-smooth px-1 sm:px-2 py-4 -mx-1">
            {blogs.map((blog, i) => {
              const hasImage =
                blog.heroImage && typeof blog.heroImage === 'object' && blog.heroImage.thumbnailURL

              return (
                <motion.div
                  key={blog.id}
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="w-[90%] sm:w-1/2 snap-center sm:min-w-[300px] max-w-sm shrink-0"
                >
                  <Link href={`/blogs/${blog.slug ?? ''}`} className="block group">
                    <div className="relative w-full h-[200px] sm:h-[250px] overflow-hidden mb-4">
                      {hasImage ? (
                        <Image   
                          src={
                            typeof blog.heroImage === 'object' &&
                            blog.heroImage !== null &&
                            blog.heroImage.thumbnailURL
                              ? blog.heroImage.thumbnailURL
                              : '/images/placeholder.png '
                          }
                          alt="No Image"
                          fill
                          className="object-cover transition-transform duration-300"
                        />
                      ) : (
                        <Image   
                          src={'/images/placeholder.png'}
                          alt="No Image"
                          fill
                          className="object-cover transition-transform duration-300"
                        />
                      )}
                    </div>
                    <div className="text-[14px] mb-1 flex gap-2">
                      <span className="text-[#2759FB] font-light">
                        {extractFirstCategoryTitle(blog.categories)}
                      </span>
                      <span className="text-gray-500">
                        {`| ${blog.publishedAt && format(new Date(blog.publishedAt), 'MMMM d, yyyy')}`}
                      </span>
                    </div>
                    <h3 className="text-[18px] sm:text-[15px] text-black font-medium">
                      {blog.title}
                    </h3>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}
